import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, Loader } from '@dreamtree-org/twreact-ui';
import { platformSend, errorOf } from './platformApi';
import { usePlatform } from './PlatformSession';
import AuthShell from '@pages/auth/AuthShell';

/**
 * Mandatory two-step setup for a platform admin's first sign-in. The QR + secret come from
 * the server once per attempt; recovery codes are shown exactly once after confirmation and
 * never again.
 */
export default function PlatformTotpSetup() {
  const nav = useNavigate();
  const { refresh, me } = usePlatform();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (me.authStrength === 'totp') return;
    platformSend('post', '/auth/totp/start')
      .then(setSetup)
      .catch((ex) => setErr(errorOf(ex).message));
  }, [me.authStrength]);

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const res = await platformSend('post', '/auth/totp/confirm', { code });
      setCodes(res.recoveryCodes);
    } catch (ex) {
      setErr(errorOf(ex).message);
    } finally {
      setBusy(false);
      setCode('');
    }
  };

  const finish = async () => {
    await refresh();
    nav('/platform', { replace: true });
  };

  if (codes) {
    return (
      <AuthShell variant="platform" title="Save your recovery codes" legalLinks={[]}>
        <div className="space-y-3">
          <Alert variant="warning" title="Shown only once">Each code signs you in once if you lose your phone. Store them in your password manager.</Alert>
          <ul className="grid grid-cols-2 gap-1 font-mono text-sm bg-secondary-50 dark:bg-secondary-900 p-3 rounded" aria-label="Recovery codes">
            {codes.map((c) => <li key={c}>{c}</li>)}
          </ul>
          <Button onClick={finish} className="w-full">I&apos;ve saved them — continue</Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      variant="platform"
      title="Set up two-step verification"
      subtitle="The platform console requires an authenticator app. Scan the code, then enter the 6 digits it shows."
      legalLinks={[]}
    >
      <div className="space-y-3">
        {err && <Alert variant="error">{err}</Alert>}
        {!setup && !err && <Loader />}
        {setup && (
          <>
            <img src={setup.qrDataUrl} alt="QR code for your authenticator app" className="mx-auto w-48 h-48 bg-white p-2 rounded" />
            <details className="text-xs text-secondary-600">
              <summary className="cursor-pointer">Can&apos;t scan? Enter this key instead</summary>
              <code className="block mt-1 break-all select-all">{setup.secretBase32}</code>
            </details>
            <form className="space-y-3" onSubmit={confirm}>
              <Input label="Code from the app" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" required />
              <Button type="submit" className="w-full" disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Turn on two-step verification'}</Button>
            </form>
          </>
        )}
      </div>
    </AuthShell>
  );
}
